export const processPacketData = (packets, timeFilter = 'daily') => {
  if (!Array.isArray(packets) || packets.length === 0) {
    return [];
  }

  if (timeFilter === 'daily') {
    return packets.map(packet => {
      const timestamp = new Date(packet.timestamp);
      return {
        timestamp: packet.timestamp,
        time: timestamp.toLocaleTimeString('en-US', {
          hour12: false,
          hour: '2-digit',
          minute: '2-digit'
        }),
        voltage: Number((parseFloat(packet.voltage) / 128).toFixed(2)),
        current: Number((parseFloat(packet.current) / 1024).toFixed(2)),
        temperature: Number(parseFloat(packet.temperature).toFixed(2)),
        soc: Number(parseFloat(packet.soc).toFixed(2)),
        health: Number(parseFloat(packet.health).toFixed(2)),
        dataPoints: packet.data_points
      };
    }).sort((a, b) => a.timestamp - b.timestamp);
  } else if (timeFilter === 'monthly') {
    return packets.map(packet => {
      const dayDate = new Date(packet.timestamp);
      return {
        timestamp: packet.timestamp,
        time: dayDate.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric'
        }),
        voltage: Number((parseFloat(packet.voltage) / 128).toFixed(2)),
        current: Number((parseFloat(packet.current) / 1024).toFixed(2)),
        temperature: Number(parseFloat(packet.temperature).toFixed(2)),
        soc: Number(parseFloat(packet.soc).toFixed(2)),
        health: Number(parseFloat(packet.health).toFixed(2)),
        dataPoints: packet.data_points // Include number of data points averaged
      };
    }).sort((a, b) => a.timestamp - b.timestamp);
  } else if (timeFilter === 'weekly') {
    // Group data by day and calculate averages
    const dailyData = packets.reduce((acc, packet) => {
      const dayDate = new Date(packet.timestamp);
      const dayKey = dayDate.toISOString().split('T')[0];
      
      if (!acc[dayKey]) {
        acc[dayKey] = {
          count: 0,
          voltage: 0,
          current: 0,
          temperature: 0,
          soc: 0,
          health: 0,
          timestamp: dayDate.getTime()
        };
      }

      acc[dayKey].count++;
      acc[dayKey].voltage += parseFloat(packet.voltage || 0);
      acc[dayKey].current += parseFloat(packet.current || 0);
      acc[dayKey].temperature += parseFloat(packet.temperature || 0);
      acc[dayKey].soc += parseFloat(packet.soc || 0);
      acc[dayKey].health += parseFloat(packet.health || 0);

      return acc;
    }, {});

    // Calculate averages and format data
    return Object.entries(dailyData)
      .map(([day, data]) => ({
        timestamp: data.timestamp,
        time: new Date(day).toLocaleDateString('en-US', {
          weekday: 'short',
          month: 'numeric',
          day: 'numeric'
        }),
        voltage: Number((data.voltage / data.count / 128).toFixed(2)),
        current: Number((data.current / data.count / 1024).toFixed(2)),
        temperature: Number((data.temperature / data.count).toFixed(2)),
        soc: Number((data.soc / data.count).toFixed(2)),
        health: Number((data.health / data.count).toFixed(2))
      }))
      .sort((a, b) => a.timestamp - b.timestamp);
  }

  return packets.map(packet => {
    try {
      // Ensure timestamp is properly formatted
      const timestamp = packet.timestamp;
      const parsedTime = new Date(typeof timestamp === 'string' ? 
        parseInt(timestamp) : timestamp);

      if (isNaN(parsedTime.getTime())) {
        console.error('Invalid timestamp:', timestamp);
        return null;
      }

      // Debug log to see raw values
      console.log("Raw packet data:", {
        voltage: packet.voltage,
        type: typeof packet.voltage
      });

      // Ensure voltage is properly parsed before division
      const rawVoltage = parseFloat(packet.voltage || '0');
      const voltage = !isNaN(rawVoltage) ? Number((rawVoltage / 128).toFixed(2)) : 0;
      const rawCurrent = parseFloat(packet.current || '0');
      const current = !isNaN(rawCurrent) ? Number((rawCurrent / 1024).toFixed(2)) : 0;
      const temperature = parseFloat(packet.temperature || 0);
      const soc = parseFloat(packet.soc || 0);
      const health = parseFloat(packet.health || 0);

      // Debug log for processed values
      console.log("Processed values:", {
        time: parsedTime.toLocaleTimeString('en-US', {
          hour12: false,
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit'
        }),
        voltage,
        rawVoltage,
        current,
        rawCurrent,
        temperature,
        soc,
        health
      });

      return {
        timestamp: parsedTime.getTime(), // Store as milliseconds
        time: parsedTime.toLocaleTimeString('en-US', {
          hour12: false,
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit'
        }),
        voltage,
        current,
        temperature,
        soc,
        health
      };
    } catch (error) {
      console.error('Error processing packet:', error, packet);
      return null;
    }
  }).filter(item => item !== null); // Remove invalid items
};